import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { agentsClient } from '@/integrations/clients';
import { IntegrationError } from '@/integrations/shared';
import { MessageDisplay } from '../types/chat-hook-types';
import { CopilotAssistant } from '../types/database-types';
import { logInfo, logError, LogCategory } from '@/utils/logger';
import { ThreadsService } from '../services/threads/threads.service';
import { MessageCrudService } from '../services/messages/message-crud.service';
import { useAIOperationWrapper } from '@/hooks/ai-usage/useAIOperationWrapper';
import { useSmartDetection } from '../functions/core/useSmartDetection';
import { useResponseParsing } from '../functions/core/useResponseParsing';

/**
 * Hook for sending messages to the copilot API with credit checking
 */
export function useMessageSending(
  user: any,
  messages: MessageDisplay[],
  setMessages: (messages: MessageDisplay[] | ((prev: MessageDisplay[]) => MessageDisplay[])) => void,
  setIsLoading: (isLoading: boolean) => void,
  setError: (error: string | null) => void,
  threadId: string | null,
  currentAssistant: CopilotAssistant | undefined
) {
  const { executeAIOperation } = useAIOperationWrapper();
  const { detectRecommendationIntent } = useSmartDetection();
  const { parseRecommendationResponse } = useResponseParsing();
  /**
   * Send a message to the chat
   */
  const sendMessage = useCallback(
    async (messageText: string, activeThreadId?: string, openaiThreadId?: string) => {
      // Use the passed activeThreadId if provided, otherwise fall back to current threadId
      const effectiveThreadId = activeThreadId || threadId;
      
      // Generate a temporary client-side ID using browser's native crypto API
      const messageId = crypto.randomUUID();

      logInfo('Sending message to agents', {
        category: LogCategory.API,
        data: { 
          userId: user?.id, 
          messageLength: messageText.length, 
          threadId: effectiveThreadId,
          assistantId: currentAssistant?.id,
          hasOpenAIThreadId: !!openaiThreadId
        }
      });

      // First add the user message locally for immediate feedback
      const userMessage: MessageDisplay = {
        id: messageId,
        threadId: effectiveThreadId || '',
        role: 'user',
        content: messageText,
        createdAt: new Date().toISOString(),
        metadata: {},
        assistant: null,
        isPending: false,
        isLoading: false
      };

      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);
      setError(null);

      try {
        // Save user message to database asynchronously and update cache with database ID
        if (effectiveThreadId && user?.id && currentAssistant?.id) {
          MessageCrudService.createMessage({
            threadId: effectiveThreadId,
            role: 'user',
            content: messageText,
            metadata: {
              assistant: {
                id: currentAssistant.id,
                name: currentAssistant.name,
                description: currentAssistant.description,
                profile_picture_url: currentAssistant.profile_picture_url,
                openai_assistant_id: currentAssistant.openai_assistant_id,
                is_active: currentAssistant.is_active,
                created_at: currentAssistant.created_at,
                updated_at: currentAssistant.updated_at
              }
            }
          }).then(savedMessage => {
            // Update the cached message with database ID for recommendation tracking
            setMessages(prev => prev.map(msg => 
              msg.id === messageId ? { ...msg, databaseId: savedMessage.id } : msg
            ));
          }).catch(error => {
            console.error('Failed to save user message to database:', error);
            // Don't block the flow - continue with OpenAI call
          });
        }

        // OpenAI threads already contain full conversation history - no need to send message history
        // This dramatically reduces payload size and prevents 502 errors on long conversations

        // Apply smart detection to enhance message if needed
        const detection = detectRecommendationIntent(messageText);
        const finalMessageText = detection.shouldEnhance ? detection.enhancedMessage : messageText;

        // Log detection results for debugging
        if (detection.shouldEnhance) {
          logInfo('Smart detection triggered', {
            category: LogCategory.API,
            data: {
              detectedTypes: detection.detectedTypes.map(d => d.type),
              confidence: detection.detectedTypes.map(d => d.confidence),
              originalLength: messageText.length,
              enhancedLength: finalMessageText.length
            }
          });
        }

        // Call the agents client with credit checking
        const data = await executeAIOperation(
          async () => {
            return await agentsClient.sendMessage(
              finalMessageText,
              effectiveThreadId || '',
              currentAssistant?.openai_assistant_id || '',
              [], // Empty array - OpenAI thread already has full conversation context
              openaiThreadId
            );
          },
          {
            immediate: true,
            errorPrefix: 'Message sending failed',
            debug: false
          }
        );

        if (!data) {
          throw new Error('No data received from function');
        }

        logInfo('Received response from agents', {
          category: LogCategory.API,
          data: {
            responseLength: data.response?.length,
            status: data.status,
            threadId: data.threadId,
            openaiThreadId: data.openaiThreadId,
            processingTime: data.processingTime
          }
        });

        // Update thread ID if this is a new conversation
        if (data.threadId && !effectiveThreadId) {
          // setThreadId(data.threadId);
        }

        // Note: OpenAI thread ID is already linked during thread creation via createThreadWithOpenAI()
        // No need to update it here since all threads are created with proper OpenAI integration

        // Parse the AI response to separate conversational and structured content
        const parsedResponse = parseRecommendationResponse(data.response);
        
        // Store full response to ensure recommendation cards work when chat is reopened
        const displayContent = data.response;
        
        // Log parsing results for debugging
        if (parsedResponse.hasRecommendation) {
          logInfo('Parsed dual-format response', {
            category: LogCategory.API,
            data: {
              hasConversationalPart: !!parsedResponse.conversationalResponse,
              hasStructuredPart: !!parsedResponse.structuredContent,
              conversationalLength: parsedResponse.conversationalResponse?.length || 0,
              structuredLength: parsedResponse.structuredContent?.length || 0,
              recommendationType: parsedResponse.recommendation?.type
            }
          });
        }

        // Add the assistant's response to the chat using the effective thread ID
        const assistantMessage: MessageDisplay = {
          id: crypto.randomUUID(),
          threadId: data.threadId || effectiveThreadId || '', // Use effective thread ID
          role: 'assistant',
          content: displayContent, // Use full response to preserve recommendation format
          createdAt: new Date().toISOString(),
          metadata: {
            tokensUsed: data.tokensUsed,
            originalResponse: data.response, // Store full response for recommendation parsing
            parsedResponse: parsedResponse, // Store parsed data for recommendation cards
            assistant: currentAssistant ? {
              id: currentAssistant.id,
              name: currentAssistant.name,
              description: currentAssistant.description,
              profile_picture_url: currentAssistant.profile_picture_url,
              openai_assistant_id: currentAssistant.openai_assistant_id,
              is_active: currentAssistant.is_active,
              created_at: currentAssistant.created_at,
              updated_at: currentAssistant.updated_at
            } : undefined
          },
          assistant: currentAssistant || null,
          isPending: false,
          isLoading: false
        };

        const assistantTempId = assistantMessage.id;
        setMessages((prev) => [...prev, assistantMessage]);

        // 🚀 FIRE AUTO-SCROLL EVENT: Trigger immediate scroll for new agent response
        // This follows the established event pattern used throughout the codebase
        window.dispatchEvent(new CustomEvent('new-agent-message', {
          detail: { 
            messageId: assistantMessage.id,
            threadId: assistantMessage.threadId,
            timestamp: assistantMessage.createdAt,
            assistantName: currentAssistant?.name || 'Assistant'
          }
        }));

        // Save assistant message to database synchronously and update cache with database ID
        if ((data.threadId || effectiveThreadId) && data.userId && currentAssistant?.id) {
          try {
            const savedMessage = await MessageCrudService.createMessage({
              threadId: data.threadId || effectiveThreadId,
              role: 'assistant',
              content: displayContent, // Use conversational part for database storage too
              metadata: {
                tokensUsed: data.tokensUsed,
                openai_run_id: data.runId,
                assistant: {
                  id: currentAssistant.id,
                  name: currentAssistant.name,
                  description: currentAssistant.description,
                  profile_picture_url: currentAssistant.profile_picture_url,
                  openai_assistant_id: currentAssistant.openai_assistant_id,
                  is_active: currentAssistant.is_active,
                  created_at: currentAssistant.created_at,
                  updated_at: currentAssistant.updated_at
                }
              }
            });
            
            // Update the cached message with database ID for recommendation tracking
            setMessages(prev => prev.map(msg => 
              msg.id === assistantTempId ? { ...msg, databaseId: savedMessage.id } : msg
            ));
            
            console.log('[MessageSending] Assistant message saved to database:', savedMessage.id);
          } catch (error) {
            console.error('Failed to save assistant message to database:', error);
            // Don't block the flow - message is already shown to user
          }
        }

        return {
          success: true,
          data
        };
      } catch (error: any) {
        const errorMsg = error.message || 'Failed to send message';
        logError('Error sending message', {
          category: LogCategory.API,
          data: { error: errorMsg, threadId: effectiveThreadId }
        });
        setError(errorMsg);
        return {
          success: false,
          error: errorMsg
        };
      } finally {
        setIsLoading(false);
      }
    },
    [user?.id, threadId, messages, setMessages, setIsLoading, setError, currentAssistant, executeAIOperation, detectRecommendationIntent, parseRecommendationResponse]
  );

  return { sendMessage };
}
